import React, { FC, useState } from 'react';
import {
  DashboardContainer, DashboardContainerBody, DashboardContainerHeader, StyledTitle
} from '../MoaDashboard.styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import SideDrawer from '../../../components/Drawer/Drawer';
import {
  useGetFailedPromotionTranslations, useGetFailedTranslations, useGetTranslations, useGetFailedMenuTranslations,
  useGetMenuTranslations, useGetPromotionTranslations
} from '../../../services/dashboard/dashboard.service';
import { DashboardFilter, BatchStatus } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams, ValueFormatterParams } from 'ag-grid-community';
import TranslationStatus from "./TranslationStatus";

interface TranslationsContainerProps {
  menuTranslation: BatchStatus
  promotionTranslation: BatchStatus
  eventTranslation: BatchStatus
  filter: DashboardFilter
  isLoading: boolean
}

const TranslationsContainer: FC<TranslationsContainerProps> = (
    {
      menuTranslation,
      promotionTranslation,
      eventTranslation,
      filter,
      isLoading
    }
) => {
  const muiTheme = createTheme();

  const [openMenuTranslationsDrawer, setOpenMenuTranslationsDrawer] = useState(false);
  const [openFailedMenuTranslationsDrawer, setOpenFailedMenuTranslationsDrawer] = useState(false);
  const [openPromotionTranslationsDrawer, setOpenPromotionTranslationsDrawer] = useState(false)
  const [openFailedPromotionTranslationsDrawer, setOpenFailedPromotionTranslationsDrawer] = useState(false);
  const [openTranslationsDrawer, setOpenTranslationsDrawer] = useState(false);
  const [openFailedTranslationsDrawer, setOpenFailedTranslationsDrawer] = useState(false);

  const { data: getMenuTranslationsResponse } = useGetMenuTranslations(filter, openMenuTranslationsDrawer);
  const menuTranslations = getMenuTranslationsResponse?.data || [];

  const { data: getFailedMenuTranslationsResponse } = useGetFailedMenuTranslations(filter, openFailedMenuTranslationsDrawer);
  const failedMenuTranslations = getFailedMenuTranslationsResponse?.data || [];

  const { data: getPromotionTranslationsResponse } = useGetPromotionTranslations(filter, openPromotionTranslationsDrawer);
  const promotionTranslations = getPromotionTranslationsResponse?.data || [];

  const { data: getFailedPromotionTranslationsResponse } = useGetFailedPromotionTranslations(filter, openFailedPromotionTranslationsDrawer);
  const failedPromotionTranslations = getFailedPromotionTranslationsResponse?.data || [];

  const { data: getTranslationsResponse } = useGetTranslations(filter, openTranslationsDrawer);
  const translations = getTranslationsResponse?.data || [];

  const { data: getFailedTranslationsResponse } = useGetFailedTranslations(filter, openFailedTranslationsDrawer);
  const failedTranslations = getFailedTranslationsResponse?.data || [];

  const hasNoTranslations: boolean =
      menuTranslation.total === 0
      && promotionTranslation.total === 0
      && eventTranslation.total === 0;

  const menuTranslationsColumnDefs = [
    {
      minWidth: 120,
      field: 'eventTime',
      headerName: 'Time',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleTimeString()),
      tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString())
    },
    {
      minWidth: 80,
      valueGetter: (params: any) => params.data.objectName.match(/(.+)-(.+)/)[1],
      headerName: 'Market',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      valueGetter: (params: any) => params.data.objectName.match(/(.+)-(.+)/)[2],
      headerName: 'Store',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 300,
      field: 'errorReason',
      headerName: 'Error Reason',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    }
  ];

  const promotionTranslationsColumnDefs = [
    {
      minWidth: 80,
      field: 'eventTime',
      headerName: 'Time',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleTimeString()),
      tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString())
    },
    {
      minWidth: 80,
      field: 'objectName',
      headerName: 'Promotion',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      field: 'status',
      headerName: 'Status',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 300,
      field: 'errorReason',
      headerName: 'Error Reason',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    }
  ];

  const eventTranslationsColumnDefs = [
    {
      minWidth: 80,
      field: 'eventTime',
      headerName: 'Time',
      sortable: true,
      floatingFilter: false,
      valueFormatter: (params: ValueFormatterParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleTimeString()),
      tooltipValueGetter: (params: ITooltipParams) => (params.value && createDateAsUTC(new Date(params.value))
          .toLocaleDateString() + ' ' + createDateAsUTC(new Date(params.value)).toLocaleTimeString())
    },
    {
      minWidth: 80,
      field: 'eventType',
      headerName: 'Event',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      field: 'objectName',
      headerName: 'Name',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      field: 'status',
      headerName: 'Status',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 300,
      field: 'errorReason',
      headerName: 'Error Reason',
      sortable: true,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    }
  ];

  const createDateAsUTC = (date: Date) => {
    return new Date(Date.UTC(
        date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(),
        date.getSeconds()
    ));
  }

  const drawers = () => {
    return (
        <ThemeProvider
            theme={muiTheme}
            key={'time-picker-mui-theme'}
        >
          <SideDrawer
              open={openMenuTranslationsDrawer}
              toggleDrawer={setOpenPromotionTranslationsDrawer}
              title={'Menu Translations'}
              columnDefs={menuTranslationsColumnDefs}
              rowData={menuTranslations}
          />
          <SideDrawer
              open={openFailedMenuTranslationsDrawer}
              toggleDrawer={setOpenFailedMenuTranslationsDrawer}
              title={'Failed Menu Translations'}
              columnDefs={menuTranslationsColumnDefs}
              rowData={failedMenuTranslations}
          />
          <SideDrawer
              open={openPromotionTranslationsDrawer}
              toggleDrawer={setOpenPromotionTranslationsDrawer}
              title={'Promotion Translations'}
              columnDefs={promotionTranslationsColumnDefs}
              rowData={promotionTranslations}
          />
          <SideDrawer
              open={openFailedPromotionTranslationsDrawer}
              toggleDrawer={setOpenFailedPromotionTranslationsDrawer}
              title={'Failed Promotion Translations'}
              columnDefs={promotionTranslationsColumnDefs}
              rowData={failedPromotionTranslations}
          />
          <SideDrawer
              open={openTranslationsDrawer}
              toggleDrawer={setOpenTranslationsDrawer}
              title={'Translations'}
              columnDefs={eventTranslationsColumnDefs}
              rowData={translations}
          />
          <SideDrawer
              open={openFailedTranslationsDrawer}
              toggleDrawer={setOpenFailedTranslationsDrawer}
              title={'Failed Translation'}
              columnDefs={eventTranslationsColumnDefs}
              rowData={failedTranslations}
          />
        </ThemeProvider>
    )
  }

  return (
      <DashboardContainer>
        <DashboardContainerHeader>Translations</DashboardContainerHeader>
        <DashboardContainerBody style={{ gap: '1rem' }}>
          {(isLoading || hasNoTranslations) &&
            <StyledTitle>No translations at this moment</StyledTitle>
          }
          <TranslationStatus
              name={'Menus'}
              translationItem={menuTranslation}
              openDetailsDrawer={() => setOpenMenuTranslationsDrawer(true)}
              openFailedDrawer={() => setOpenFailedMenuTranslationsDrawer(true)}
              isLoading={isLoading}
          />
          <TranslationStatus
              name={'Coupons'}
              translationItem={promotionTranslation}
              openDetailsDrawer={() => setOpenPromotionTranslationsDrawer(true)}
              openFailedDrawer={() => setOpenFailedPromotionTranslationsDrawer(true)}
              isLoading={isLoading}
          />
          <TranslationStatus
              name={'Events'}
              translationItem={eventTranslation}
              openDetailsDrawer={() => setOpenTranslationsDrawer(true)}
              openFailedDrawer={() => setOpenFailedTranslationsDrawer(true)}
              isLoading={isLoading}
          />
        </DashboardContainerBody>
        {drawers()}
      </DashboardContainer>
  );
};

export default TranslationsContainer;